import { lazy } from 'react';
import type { Feature } from '../types/appConfig';
import { FEATURE_IDS, COMPONENT_IDS, PAGE_PATHS } from '../constants/appConstants';

/**
 * Features define the functional capabilities available in the application.
 * Each feature contains:
 * - Pages that can be accessed
 * - Panel components for dynamic page panels based on chat input
 * - Widgets for dashboard/standalone pages
 *
 * Access control is now handled by backend feature flags via /api/v1/profile/me
 */
export const FEATURES: Feature[] = [
  {
    id: FEATURE_IDS.CORE_NAVIGATION,
    name: 'Core Navigation',
    description: 'Basic navigation and dashboard functionality available to all users',
    pages: [
      {
        path: PAGE_PATHS.DASHBOARD,
        name: 'Dashboard',
        description: 'Main dashboard with chat and dynamic panels',
        showInNavigation: true,
      },
      {
        path: PAGE_PATHS.DEBUG,
        name: 'Debug',
        description: 'Development debugging tools',
        showInNavigation: false,
      },
      {
        path: PAGE_PATHS.COMPONENTS,
        name: 'Components',
        description: 'Component showcase for development',
        showInNavigation: false,
      },
      {
        path: PAGE_PATHS.TESTER_AID,
        name: 'Tester Aid',
        description: 'Self-service tools for the testing team',
        showInNavigation: false,
      },
    ],
    panelComponents: [
      {
        id: COMPONENT_IDS.MY_ACCOUNT_PANEL,
        name: 'My Account',
        description: 'Manage your account and delegate access',
        component: lazy(() => import('../../account-management').then((m) => ({ default: m.MyAccountPanel }))),
      },
    ],
    widgets: [],
  },
  {
    id: FEATURE_IDS.ACTION_ITEMS,
    name: 'Action Items',
    description: 'Task management and planning',
    pages: [],
    panelComponents: [
      {
        id: COMPONENT_IDS.ACTION_ITEMS_PANEL,
        name: 'Tasks',
        description: 'Task management and planning',
        component: lazy(() => import('../../tasks').then((m) => ({ default: m.TasksPanel }))),
      },
    ],
    widgets: [
      {
        id: COMPONENT_IDS.TASKS_WIDGET,
        name: 'Tasks',
        description: 'Task management widget showing overdue, this week and recommended tasks',
        component: lazy(() => import('../../tasks').then((m) => ({ default: m.TasksWidget }))),
      },
    ],
  },
  {
    id: FEATURE_IDS.MORNING_DASHBOARD,
    name: 'Morning Dashboard',
    description: 'Morning dashboard overview with daily summary, tasks, calendar and productivity tools',
    pages: [],
    panelComponents: [
      {
        id: COMPONENT_IDS.MORNING_DASHBOARD_PANEL,
        name: 'Morning Dashboard',
        description: 'Morning overview panel with daily summary',
        component: lazy(() => import('../../morning-dashboard').then((m) => ({ default: m.MorningDasboard }))),
      },
    ],
    widgets: [
      {
        id: COMPONENT_IDS.COLLEAGUES_WIDGET,
        name: 'Colleagues',
        description: 'Colleague contact management widget',
        component: lazy(() => import('../../morning-dashboard').then((m) => ({ default: m.ColleaguesWidget }))),
      },
      {
        id: COMPONENT_IDS.SHORTCUTS_WIDGET,
        name: 'Shortcuts',
        description: 'Quick shortcuts widget for frequently accessed resources',
        component: lazy(() => import('../../morning-dashboard').then((m) => ({ default: m.ShortcutsWidget }))),
      },
    ],
  },
  {
    id: FEATURE_IDS.DIARY_MANAGEMENT,
    name: 'Diary Management',
    description: 'Diary management and scheduling capabilities',
    pages: [],
    panelComponents: [
      {
        id: COMPONENT_IDS.DIARY_MANAGEMENT_PANEL,
        name: 'Diary Management',
        description: 'Simple calendar and scheduling management panel',
        component: lazy(() => import('../../diary-management').then((m) => ({ default: m.DiaryManagementPanel }))),
      },
    ],
    widgets: [
      {
        id: COMPONENT_IDS.MEETING_WIDGET,
        name: 'Meetings',
        description: 'Calendar and meeting overview widget',
        component: lazy(() => import('../../diary-management').then((m) => ({ default: m.MeetingWidget }))),
      },
    ],
  },
  {
    id: FEATURE_IDS.THREAD_SUMMARISATION,
    name: 'Thread Summarisation',
    description: 'Email thread summarisation and analysis',
    pages: [],
    panelComponents: [
      {
        id: COMPONENT_IDS.THREAD_SUMMARISATION_PANEL,
        name: 'Thread Summarisation',
        description: 'Summarise email threads from inbox or uploaded files',
        component: lazy(() => import('../../thread-summarisation').then((m) => ({ default: m.SummarisationPanel }))),
      },
    ],
    widgets: [],
  },
  {
    id: FEATURE_IDS.MEETING_AVAILABILITY,
    name: 'Meeting Availability',
    description: 'Schedule meetings availability across calendars',
    pages: [],
    panelComponents: [
      {
        id: COMPONENT_IDS.MEETING_AVAILABILITY_PANEL,
        name: 'Meeting Availability',
        description: 'Find available meeting times across calendars',
        component: lazy(() =>
          import('../../meeting-availability').then((m) => ({ default: m.MeetingAvailabilityPanel })),
        ),
      },
    ],
    widgets: [],
  },
  {
    id: FEATURE_IDS.HANDOVER_SUPPORT,
    name: 'Handover Support',
    description: 'Manage and track handover items',
    pages: [],
    panelComponents: [
      {
        id: COMPONENT_IDS.HANDOVER_SUPPORT_PANEL,
        name: 'Handover Support',
        description: 'View and manage handover items',
        component: lazy(() => import('../../handover-support').then((m) => ({ default: m.HandoverSupportPanel }))),
      },
    ],
    widgets: [],
  },
  {
    id: FEATURE_IDS.ROBIN_CHAT,
    name: 'Robin Chat',
    description: 'AI-powered chat with streaming responses (POC)',
    pages: [
      {
        path: PAGE_PATHS.ROBIN_CHAT,
        name: 'Robin Chat',
        description: 'Full-page streaming chat interface',
        showInNavigation: true,
      },
    ],
    panelComponents: [],
    widgets: [],
  },
  {
    id: FEATURE_IDS.INSIGHTS_HUB,
    name: 'Insights Hub',
    description: 'Insights and analytics hub',
    pages: [
      {
        path: PAGE_PATHS.INSIGHTS_HUB,
        name: 'Insights Hub',
        description: 'Insights and analytics hub',
        showInNavigation: false,
      },
    ],
    panelComponents: [
      {
        id: COMPONENT_IDS.INSIGHTS_HUB_PANEL,
        name: 'Insights Hub',
        description: 'Insights and analytics hub',
        component: lazy(() => import('../../insights-hub').then((m) => ({ default: m.InsightsHubPanel }))),
      },
    ],
    widgets: [],
  },
];
